import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { Eye, EyeOff, Lock } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../common/components/Button";
import PageHeader from "../../common/components/PageHeader";
import { SPACING } from "../../common/constants/spacing";
import { changePassword } from "../api/user";

function SecurityPage() {
  const { t } = useTranslation("account");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changePasswordDialog, setChangePasswordDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const handlePasswordChange = (field, value) => {
    setPasswordForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePasswordSubmit = async () => {
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      await changePassword(
        passwordForm.currentPassword,
        passwordForm.newPassword,
      );
      setSuccess(t("security.changePassword.success"));
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setTimeout(() => {
        setChangePasswordDialog(false);
        setSuccess("");
      }, 2000);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = () => {
    setChangePasswordDialog(false);
    setError("");
    setSuccess("");
    setPasswordForm({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      <PageHeader
        title={t("security.title")}
        description={t("security.description")}
      />

      <Grid container spacing={3}>
        {/* Password Section */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Lock
                  size={20}
                  color="currentColor"
                  style={{ marginRight: 16, color: "primary.main" }}
                />
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  {t("security.password.title")}
                </Typography>
              </Box>
              <Typography variant="bodySm" color="text.secondary">
                {t("security.password.description")}
              </Typography>
              <Button
                variant="outlined"
                sx={{ mt: 3 }}
                fullWidth
                onClick={() => setChangePasswordDialog(true)}
              >
                {t("security.password.changeButton")}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Change Password Dialog */}
      <Dialog
        open={changePasswordDialog}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle variant="h4" sx={{ mt: 1 }}>
          {t("security.changePassword.title")}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: SPACING.sm }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: SPACING.sm }}>
                {success}
              </Alert>
            )}
            <TextField
              fullWidth
              label={t("security.changePassword.currentPassword")}
              type={showCurrentPassword ? "text" : "password"}
              value={passwordForm.currentPassword}
              onChange={(e) =>
                handlePasswordChange("currentPassword", e.target.value)
              }
              sx={{ mb: SPACING.sm }}
              InputProps={{
                endAdornment: (
                  <Button
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    sx={{ minWidth: "auto", p: 1 }}
                  >
                    {showCurrentPassword ? (
                      <EyeOff size={16} />
                    ) : (
                      <Eye size={16} />
                    )}
                  </Button>
                ),
              }}
            />
            <TextField
              fullWidth
              label={t("security.changePassword.newPassword")}
              type={showNewPassword ? "text" : "password"}
              value={passwordForm.newPassword}
              onChange={(e) =>
                handlePasswordChange("newPassword", e.target.value)
              }
              sx={{ mb: SPACING.sm }}
              helperText={t("security.changePassword.passwordRequirement")}
              InputProps={{
                endAdornment: (
                  <Button
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    sx={{ minWidth: "auto", p: 1 }}
                  >
                    {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </Button>
                ),
              }}
            />
            <TextField
              fullWidth
              label={t("security.changePassword.confirmPassword")}
              type={showConfirmPassword ? "text" : "password"}
              value={passwordForm.confirmPassword}
              onChange={(e) =>
                handlePasswordChange("confirmPassword", e.target.value)
              }
              InputProps={{
                endAdornment: (
                  <Button
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    sx={{ minWidth: "auto", p: 1 }}
                  >
                    {showConfirmPassword ? (
                      <EyeOff size={16} />
                    ) : (
                      <Eye size={16} />
                    )}
                  </Button>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="secondary"
            onClick={handleDialogClose}
            disabled={loading}
          >
            {t("security.changePassword.cancel")}
          </Button>
          <Button
            onClick={handlePasswordSubmit}
            variant="contained"
            disabled={
              loading ||
              !passwordForm.currentPassword ||
              !passwordForm.newPassword ||
              passwordForm.newPassword.length < 8 ||
              passwordForm.newPassword !== passwordForm.confirmPassword
            }
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading
              ? t("security.changePassword.changing")
              : t("security.changePassword.submit")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SecurityPage;
